import { Link } from '@tanstack/react-router';
import { ArrowLeft, ChevronDown, Download } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Money } from '../../components/Money';
import { Skeleton } from '../../components/Skeleton';
import { cn } from '../../lib/cn';
import { formatDate } from '../../lib/format';
import { usePackageSubscribers } from '../../api/admin';

const STATUS_TONE = {
  active: 'success' as const,
  trial: 'info' as const,
  past_due: 'warning' as const,
  canceled: 'error' as const,
};

const STATUS_LABEL = {
  active: 'Active',
  trial: 'Trial',
  past_due: 'Past due',
  canceled: 'Canceled',
};

/** Subscribers per package — by-price chart, by-status, by-reseller, subscriber table. */
export function PackageSubscribers() {
  const { data: subscribers = [], isLoading } = usePackageSubscribers();
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <Link to="/admin/packages" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-4 transition">
        <ArrowLeft className="w-4 h-4" />
        All packages
      </Link>

      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1.5">Pricing</p>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
          Pro — subscribers <span className="text-zinc-400 dark:text-zinc-500 font-medium tabular-nums">(89 active)</span>
        </h1>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">Critical view for grandfathering decisions and migration planning.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <Card className="p-5 sm:p-6">
          <CardHeader title="By price" subtitle="Multi-currency, multi-interval" />
          <div className="space-y-3">
            <PriceBar label="BDT Monthly ৳1,500" count={67} pct={75} />
            <PriceBar label="BDT Yearly ৳15,000" count={22} pct={25} />
            <PriceBar label="USD Monthly $15" count={0} pct={0} />
            <PriceBar label="USD Yearly $150" count={0} pct={0} />
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="By status" />
          <div className="grid grid-cols-2 gap-3">
            <StatusStat n={81} label="Active" color="emerald" />
            <StatusStat n={5} label="Trial" color="amber" />
            <StatusStat n={2} label="Past due" color="rose" />
            <StatusStat n={1} label="Canceled" color="zinc" />
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="By reseller" />
          <ul className="space-y-2.5 text-sm">
            <ResellerRow name="Acme Resellers" count={34} pct={38} />
            <ResellerRow name="Sundarban Tech" count={21} pct={24} />
            <ResellerRow name="Direct (no reseller)" count={19} pct={21} />
            <ResellerRow name="CityPOS Bangla" count={11} pct={12} />
            <ResellerRow name="Other 4 resellers" count={4} pct={5} />
          </ul>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mr-auto">
            Subscriber list
          </h2>
          <Filter label="Status" value="All" />
          <Filter label="Price" value="All" />
          <Filter label="Reseller" value="All" />
          <Button variant="secondary" size="sm" iconLeft={<Download className="w-3.5 h-3.5" />}>
            Export CSV
          </Button>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Business</th>
                <th className="text-left font-medium px-4 py-3">Reseller</th>
                <th className="text-left font-medium px-4 py-3">Price</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Renews</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3"><Skeleton className="h-4 w-40 max-w-full rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-28 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-20 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-5 sm:px-6 py-3"><Skeleton className="h-4 w-24 rounded" /></td>
                  </tr>
                ))
              ) : subscribers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No subscribers found.
                  </td>
                </tr>
              ) : (
                subscribers.map((s) => (
                <tr key={s.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-5 sm:px-6 py-3 font-medium text-zinc-950 dark:text-zinc-50">{s.business}</td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{s.reseller}</td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300">{s.price}</td>
                  <td className="px-4 py-3">
                    <Badge tone={STATUS_TONE[s.status as keyof typeof STATUS_TONE]}>
                      {STATUS_LABEL[s.status as keyof typeof STATUS_LABEL]}
                    </Badge>
                  </td>
                  <td className="px-5 sm:px-6 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{formatDate(s.renews)}</td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function PriceBar({ label, count, pct }: { label: string; count: number; pct: number }) {
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-zinc-700 dark:text-zinc-200">{label}</span>
        <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400">{count}</span>
      </div>
      <div className="h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
        <div className="h-full bg-brand dark:bg-brand-dm rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function StatusStat({ n, label, color }: { n: number; label: string; color: 'emerald' | 'amber' | 'rose' | 'zinc' }) {
  const cls = {
    emerald: 'text-emerald-700 dark:text-emerald-300',
    amber: 'text-amber-700 dark:text-amber-300',
    rose: 'text-rose-700 dark:text-rose-300',
    zinc: 'text-zinc-700 dark:text-zinc-200',
  }[color];
  return (
    <div className="rounded-md p-3 bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
      <div className={cn('text-2xl font-semibold tabular-nums', cls)}>{n}</div>
      <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{label}</div>
    </div>
  );
}

function ResellerRow({ name, count, pct }: { name: string; count: number; pct: number }) {
  return (
    <li>
      <div className="flex items-center justify-between mb-1">
        <span className="text-zinc-700 dark:text-zinc-200">{name}</span>
        <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400">{count} <span className="opacity-60">({pct}%)</span></span>
      </div>
      <div className="h-1 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
        <div className="h-full bg-zinc-500 dark:bg-zinc-400 rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </li>
  );
}

function Filter({ label, value }: { label: string; value: string }) {
  return (
    <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition">
      <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
      <span>{value}</span>
      <ChevronDown className="w-3 h-3 text-zinc-400" />
    </button>
  );
}
